import './table.scss';

import { useCollection } from '@amzn/awsui-collection-hooks';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { Button, ButtonProps, Pagination, PropertyFilter, Table, TableProps } from '@amzn/awsui-components-react';
import { CancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import * as React from 'react';

import { formattedTableHeaders } from '../../components/common/awsui-table-helper';
import RumContext from '../../contexts/RumContext';
import { convertToString } from '../util';
import { localization, paginationLabels, PROPERTY_FILTERING_I18N_CONSTANTS } from './constants';
import { buildTableFilterProperties, buildTablePreference, 
    formatValueForFilter, generateFilterCounterText, generateVisibleContents
} from './helper';
import DownloadModal from './Table-download';
import TableEmptyState from './Table-empty';
import { default as TableHeader } from './Table-header';
import TableNoMatchState from './Table-nomatch';
import { default as Preferences } from './Table-preference';
import { ColumnDefinition, CustomPropertyFilterGroup, Operator, PropertyFilteringOption, Query, VisibleContentOption } from './types';

interface AWSCATTableProps<T> {
    resourceName: string, // Resource name
    resourceType?: string, // Resource type, singular
    items: T[], // Table items
    columnDefinitions: ColumnDefinition<T>[], // Column definition
    trackBy?: string, // Unique Id of items, used to improve performance
    resizeable?: boolean, // To indicate whether table cell is resizeable
    visibleContent?: VisibleContentOption[], // To indicate which columns are visible, and editable
    defaultPageSize?: number,
    headerActions?: React.ReactNode; // Action buttons on table header
    headerInfo?: React.ReactNode; // Info link on table header
    headerDescription?: React.ReactNode; // description on table header
    loading?: boolean;
    downloadFn?: CancelableEventHandler<ButtonProps.ClickDetail>; // Customized download function
    useDefaultDownload?: boolean; // Use the default papaparse download csv function
    downloadFileName?: string;
    selectionType?: TableProps.SelectionType; // If undefined, then no selection for the table
    onSelectionChange?: (items: readonly T[] | undefined) => void;
    onFilterChange?: (items: readonly T[] | null, query?: Query) => void; // called when the filtered items change
    onSortingChange?: (isDescending?: boolean, sortingField?: string) => void; // called when sort order is changed
    enableAutoSorting?: boolean; // Automatically enable sorting on all columns
    autoGenerateVisibleContent?: boolean; // Automatically generate visible content from column definition
    defaultSortBy?: SortingState<T>; // Sort by which column by default
    defaultHiddenColumns?: string[]; // Hidden columns by default
    defaultQuery?: Query; // Pass in filters programmatically
    filterTokenDisplayLimit?: number; // If set, will hide filtering tokens above limit. user can click 'show more' to see all
    emptyTable?: JSX.Element; //element to display when the table is empty
    importWhenEmpty?: boolean;
    customPropertyFilterGroup?: CustomPropertyFilterGroup<T>; //used for custom group property filter
}

function AWSCATTable<T>(props: AWSCATTableProps<T>): JSX.Element {
    const { onSelectionChange, onFilterChange } = props;
    const [ preferences, setPreferences ] = React.useState(buildTablePreference(undefined, props.defaultPageSize));
    const [ downloadModalVisible, setDownloadModalVisible] = React.useState(false);
    const visibleContents: VisibleContentOption[] = React.useMemo(() => {
        return props.visibleContent ? props.visibleContent :
            (props.autoGenerateVisibleContent ? generateVisibleContents(props.columnDefinitions) : []);
    }, [props.visibleContent, props.autoGenerateVisibleContent, props.columnDefinitions]);

    const { addEvent, recordEvent } = React.useContext(RumContext);

    React.useEffect(() => {
        if (visibleContents?.length) {
            const visibleColumns = visibleContents
                .filter(content => (!props.defaultHiddenColumns?.includes(content.id)))
                .map(content => content.id);
            const defaultPreference = buildTablePreference(visibleColumns);
            setPreferences(defaultPreference);
        }
    }, [ visibleContents, props.defaultHiddenColumns ]);

    const filteringProperties = buildTableFilterProperties<T>(props.columnDefinitions, props.customPropertyFilterGroup);
    const onClearFilter = () => {
        actions.setPropertyFiltering({ tokens: [], operation: 'and' });
    };

    const getPreferenceComponent = (): JSX.Element | null => {
        if (props.items.length !== 0) {
            if(props.downloadFn || props.useDefaultDownload) {
                const downloadOnclick = props.downloadFn ? props.downloadFn : () => setDownloadModalVisible(true);
                return (
                    <div>
                        <div id="tablePreference">
                            <Preferences
                                preferences={preferences}
                                setPreferences={addEvent(setPreferences, {type: `${props.resourceName}TablePreferences`, data: {user_interaction: 'click'}})}
                                visibleContentOptions={visibleContents}
                            />
                        </div>
                        <Button
                            data-id='btn-table-download'
                            iconName="download"
                            variant="icon"
                            loading={props.loading}
                            onClick={downloadOnclick}
                        />
                    </div>
                );
            } else {
                return (
                    <Preferences
                        preferences={preferences}
                        setPreferences={setPreferences}
                        visibleContentOptions={visibleContents}
                    />
                );
            }
        } else {
            return null;
        }
    };

    // copied from awsui-collection-hooks, it is not exported
    // updated to correctly compare numbers
    const filterUsingOperator = (itemValue: string | number, tokenValue: string | number, operator: Operator): boolean => {
        switch (operator) {
            case '<':
                return itemValue < tokenValue;
            case '<=':
                return itemValue <= tokenValue;
            case '>':
                return itemValue > tokenValue;
            case '>=':
                return itemValue >= tokenValue;
            case '=':
                // eslint-disable-next-line eqeqeq
                return itemValue == tokenValue;
            case '!=':
                // eslint-disable-next-line eqeqeq
                return itemValue != tokenValue;
            case ':':
                return (itemValue + '').toLowerCase().indexOf((tokenValue + '').toLowerCase()) > -1;
            case '!:':
                return (itemValue + '').toLowerCase().indexOf((tokenValue + '').toLowerCase()) === -1;
        }
    };
  
    /**
     * Filters item based on the tokenValue
     * if propertyKey is present, it filters based on that property, else looks for the value in all properties
     * for each item property, it references the data function in the column definition to extract the value to compare
     * if not present, it uses the default value present in the item for that field
     * @param  {T} item
     * @param  {string} tokenValue
     * @param  {Operator} operator
     * @param  {string} propertyKey?
     * @returns boolean
     */
    const propertyFilter = (item: T, tokenValue: string, operator: Operator, propertyKey?: string): boolean => {
        if (propertyKey) {
            let columnDefinition = props.columnDefinitions.find(columnDef => columnDef.id === propertyKey);
            if (!columnDefinition && props.customPropertyFilterGroup) { //check the custom group definitions
                columnDefinition = props.customPropertyFilterGroup.propertyDefinitions.find(columnDef => columnDef.id === propertyKey);
            }
            const formattedToken = formatValueForFilter(tokenValue, columnDefinition?.type);
            const itemValue = columnDefinition?.data ? columnDefinition.data(item) : (item[propertyKey as keyof T] as any as string | number);
            const formattedItemValue = formatValueForFilter(itemValue, columnDefinition?.type);

            return filterUsingOperator(formattedItemValue, formattedToken, operator);
        } else {
            return props.columnDefinitions.some(columnDef => {
                const itemValue = convertToString(columnDef.data ? columnDef.data(item) : item[(columnDef.id) as keyof T]);
                return filterUsingOperator(itemValue, tokenValue, operator);
            });
        }
    };

    /**
     * Generate the filtering options - the values shown to the user after selecting the column and operator in the property filter
     * useCollection creates the filteringOptions based on the item values, not the text the user sees
     * for each generated option, apply the column definition manipulation, if exists, for that column on an item with the same value
     * @returns array of property filtering options
     */
    const generateFilteringOptions = (): PropertyFilteringOption[] => {
        const options: PropertyFilteringOption[] = [];
        props.columnDefinitions.forEach(col => {
            //check if column is a group and add options based on nested column definition
            if (props.customPropertyFilterGroup?.group  === col.id) {
                props.customPropertyFilterGroup.propertyDefinitions.forEach(groupCol => {
                    const colOptions = getFilterOptionsForOneColumn(groupCol);
                    options.push(...colOptions);
                });
            } else {
                const colOptions = getFilterOptionsForOneColumn(col);
                options.push(...colOptions);
            }
        });
        return options;
    };

    /**
     * Generate the filtering options for the given column, based on the values present in the items
     * @param col 
     * @returns 
     */
    const getFilterOptionsForOneColumn = (col: ColumnDefinition<T>): PropertyFilteringOption[] => {
        const options: PropertyFilteringOption[] = [];
        const values: Set<string> = new Set(); // each col, value pair should be unique
        props.items.forEach(item => {
            values.add(col.data ? col.data(item)?.toString() : (item[col.id as keyof T] as any)?.toString());
        });
        values.forEach(value => {
            options.push({
                propertyKey: col.id,
                value
            });
        });
        return options;
    };

    // Custom filter function to allow us to filter based on columns that are derived values
    /**
     * @param  {T} item
     * @param  {Query} query
     * @returns boolean
     */
    const filteringFunction = (item: T, query: Query): boolean => {
        const { operation, tokens } = query;
        if (!tokens.length) {
            return true;
        } else if  (operation === 'and') {
            return tokens.every(({ value, operator, propertyKey }) => {
                return propertyFilter(item, value, operator, propertyKey);
            });
        } else if (operation === 'or') {
            return tokens.some(({ value, operator, propertyKey }) => {
                return propertyFilter(item, value, operator, propertyKey);
            });
        } else {
            return true;
        }
    };

    const { items, actions, filteredItemsCount, collectionProps, paginationProps, propertyFilterProps } = useCollection(
        props.items,
        {
            propertyFiltering: {
                filteringProperties: filteringProperties,
                empty: props.emptyTable ??
                    (<TableEmptyState resourceName={props.resourceName} resourceType={props?.resourceType} import={props.importWhenEmpty}/>),
                noMatch: <TableNoMatchState onClearFilter={onClearFilter} />,
                filteringFunction,
                defaultQuery: props.defaultQuery,
            },
            pagination: { pageSize: props.defaultPageSize ?? preferences.pageSize },
            sorting: {
                defaultState: props.defaultSortBy,
            },
            selection: {}
        }
    );
    React.useEffect(() => {
        if (paginationProps.currentPageIndex > 1) {
            // record a RUM event when a user uses pagination to see more pages
            recordEvent({data: {user_interaction: 'pagination'}, type: `${props.resourceName}TablePagination`});
        }
    }, [paginationProps.currentPageIndex, props.resourceName, recordEvent]);

    React.useEffect(() => {
        actions.setPropertyFiltering(props.defaultQuery || {tokens: [], operation: 'and'});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.defaultQuery]);
    
    // It's a workaround to get all filtered items
    const filteringCollection = useCollection(
        props.items,
        {
            pagination: { pageSize: Number.MAX_SAFE_INTEGER },
            propertyFiltering: { filteringProperties: filteringProperties },
        }
    );
    React.useEffect(() => {
        if (onFilterChange) {
            if (filteringCollection.propertyFilterProps.query.tokens.length === 0) {
                onFilterChange(null, undefined); // no filter is set
            } else {
                onFilterChange(filteringCollection.items, filteringCollection.propertyFilterProps.query);
                // record a RUM event when user filters a table
                recordEvent({type: `${props.resourceName}TableFilter`, data: {user_interaction: 'filter'}});
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ filteringCollection.propertyFilterProps.query, filteredItemsCount ]);
    const updatedQuery = propertyFilterProps.query;
    React.useEffect(() => {
        filteringCollection.propertyFilterProps.onChange({ detail: updatedQuery });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ updatedQuery ]);

    React.useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(collectionProps.selectedItems);
        }
        // record a RUM event when the selected items change
        recordEvent({type: `${props.resourceName}TableItemSelect`, data: {user_interaction: 'select'}});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectionProps.selectedItems]);

    const colDefs = props.enableAutoSorting ? props.columnDefinitions.map(def => ({...def, sortingField: def.sortingField || def.id})): props.columnDefinitions;
    return (
        <>
            <Table
                {...collectionProps}
                items={items}
                columnDefinitions={formattedTableHeaders(colDefs)}
                trackBy={props.trackBy}
                resizableColumns={props.resizeable}
                selectedItems={collectionProps.selectedItems}
                loading={props.loading}
                loadingText={localization.LOADING  + ' ' + props.resourceName}
                selectionType={items.length !== 0 ? props.selectionType : undefined}
                visibleColumns={preferences.visibleContent}
                stickyHeader={true}
                wrapLines={preferences.wrapLines}
                filter={
                    <PropertyFilter
                        i18nStrings={PROPERTY_FILTERING_I18N_CONSTANTS}
                        {...propertyFilterProps}
                        filteringOptions={generateFilteringOptions()}
                        countText={generateFilterCounterText(filteredItemsCount)}
                        tokenLimit={props.filterTokenDisplayLimit}
                        customGroupsText={props.customPropertyFilterGroup ? [{
                            properties: props.customPropertyFilterGroup.properties,
                            values: props.customPropertyFilterGroup.values,
                            group: props.customPropertyFilterGroup.group
                        }]: undefined}
                    />
                }
                header={
                    <TableHeader
                        resourceName={props.resourceName}
                        totalItemCount={props.items.length}
                        selectedItemsCount={collectionProps.selectedItems?.length}
                        actions={props.headerActions}
                        info={props.headerInfo}
                        description={props.headerDescription}
                    />
                }
                pagination={
                    <Pagination
                        {...paginationProps}
                        ariaLabels={paginationLabels}
                    />
                }
                preferences={getPreferenceComponent()}
                onSortingChange={event => {
                    if (props.onSortingChange){ // Pass the sort details to the page for filtered downloads
                        props.onSortingChange(event.detail.isDescending, event.detail.sortingColumn.sortingField);
                    }
                    if (collectionProps.onSortingChange){
                        collectionProps.onSortingChange(event); // Pass the event to default sort to sort the column
                    }
                }}
            />
            { downloadModalVisible && (
                <DownloadModal
                    visible={downloadModalVisible}
                    setVisible={setDownloadModalVisible}
                    columnDefinition={props.columnDefinitions}
                    includedColumns={preferences.visibleContent}
                    singlePageItems={items}
                    allItems={props.items}
                    filteredItems={filteringCollection.propertyFilterProps.query.tokens.length === 0 ? [] : filteringCollection.items}
                    selectedItems={collectionProps.selectedItems || []}
                    fileName={props.downloadFileName || props.resourceName}
                ></DownloadModal>
            )}
        </>
    );
}

export default AWSCATTable;
